/**
 * Full-bleed hero scene: a worn-down house rides the conveyor, gets worked on
 * with a swinging hammer mid-renovation, and comes out spotless with green
 * sparkles — Takarah puts your properties in order. Pure CSS/SVG, no external
 * assets. Scales down via transform on small screens; animations respect
 * reduced motion.
 */

function ShabbyHouse() {
  return (
    <svg viewBox="0 0 140 120" className="w-[170px] drop-shadow-[0_24px_30px_rgba(20,20,20,0.18)]">
      {/* sagging roof */}
      <polygon points="12,52 70,12 128,56 120,60 70,24 20,58" fill="#B9AE9C" />
      {/* crooked chimney */}
      <rect x="96" y="22" width="12" height="22" fill="#C4B9A6" transform="rotate(6 102 33)" />
      {/* body */}
      <rect x="24" y="54" width="92" height="58" fill="#DCD3C2" />
      {/* cracks */}
      <path d="M38 60 l6 10 -4 8 5 9" stroke="#B0A48F" strokeWidth="2" fill="none" strokeLinecap="round" />
      <path d="M104 66 l-5 8 4 7" stroke="#B0A48F" strokeWidth="2" fill="none" strokeLinecap="round" />
      {/* broken window */}
      <rect x="34" y="80" width="18" height="18" rx="2" fill="#8F8672" />
      <path d="M36 82 l14 14 M50 82 l-14 14" stroke="#DCD3C2" strokeWidth="2" />
      {/* boarded window */}
      <rect x="88" y="80" width="18" height="18" rx="2" fill="#8F8672" />
      <rect x="86" y="86" width="22" height="5" rx="1" fill="#C4B9A6" transform="rotate(-8 97 88)" />
      {/* tilted door */}
      <rect x="62" y="78" width="18" height="34" rx="2" fill="#9A8F79" transform="rotate(-4 71 95)" />
    </svg>
  );
}

function MidRenoHouse() {
  return (
    <svg viewBox="0 0 140 120" className="w-[190px] drop-shadow-[0_26px_32px_rgba(20,20,20,0.2)]">
      {/* roof: old half + new half */}
      <polygon points="12,54 70,10 70,22 22,56" fill="#B9AE9C" />
      <polygon points="70,10 128,54 118,54 70,20" fill="#141414" />
      <rect x="98" y="20" width="12" height="24" rx="2" fill="#2A2A28" />
      {/* body */}
      <rect x="24" y="52" width="92" height="60" rx="3" fill="#F3EFE6" />
      {/* old boarded window (left, pending) */}
      <rect x="34" y="62" width="20" height="20" rx="3" fill="#BFB7A4" />
      <rect x="32" y="69" width="24" height="5" rx="1" fill="#D8CFBC" transform="rotate(-8 44 71)" />
      {/* new window (right, done) */}
      <rect x="86" y="62" width="20" height="20" rx="3" fill="#DDF3E4" stroke="#22A65B" strokeWidth="2" />
      <path d="M96 62 v20 M86 72 h20" stroke="#22A65B" strokeWidth="1.5" />
      {/* door half painted */}
      <rect x="60" y="76" width="20" height="36" rx="3" fill="#9A8F79" />
      <path d="M60 79 a3 3 0 0 1 3-3 h7 v36 h-7 a3 3 0 0 1 -3-3 z" fill="#22A65B" />
      {/* paint bucket at the door */}
      <rect x="106" y="98" width="14" height="14" rx="2" fill="#FFFFFF" stroke="#141414" strokeWidth="1.5" />
      <path d="M106 102 h14" stroke="#22A65B" strokeWidth="3" />
    </svg>
  );
}

function ShinyHouse() {
  return (
    <svg viewBox="0 0 140 120" className="w-[180px] drop-shadow-[0_28px_34px_rgba(34,166,91,0.22)]">
      {/* crisp roof */}
      <polygon points="12,54 70,10 128,54 118,54 70,20 22,54" fill="#141414" />
      <rect x="98" y="20" width="12" height="24" rx="2" fill="#2A2A28" />
      {/* body */}
      <rect x="24" y="52" width="92" height="60" rx="3" fill="#FFFFFF" />
      {/* windows */}
      <rect x="34" y="62" width="20" height="20" rx="3" fill="#DDF3E4" stroke="#22A65B" strokeWidth="2" />
      <path d="M44 62 v20 M34 72 h20" stroke="#22A65B" strokeWidth="1.5" />
      <rect x="86" y="62" width="20" height="20" rx="3" fill="#DDF3E4" stroke="#22A65B" strokeWidth="2" />
      <path d="M96 62 v20 M86 72 h20" stroke="#22A65B" strokeWidth="1.5" />
      {/* green door */}
      <rect x="60" y="76" width="20" height="36" rx="3" fill="#22A65B" />
      <circle cx="76" cy="95" r="1.8" fill="#FFFFFF" />
    </svg>
  );
}

function Hammer() {
  return (
    <svg viewBox="0 0 120 130" className="w-[110px] drop-shadow-[0_14px_18px_rgba(20,20,20,0.25)]">
      {/* handle */}
      <rect x="53" y="34" width="14" height="90" rx="6" fill="#C9A06A" />
      <rect x="53" y="104" width="14" height="20" rx="6" fill="#B08A55" />
      {/* head */}
      <rect x="24" y="10" width="72" height="28" rx="8" fill="#141414" />
      <rect x="24" y="10" width="18" height="28" rx="8" fill="#2E2E2B" />
    </svg>
  );
}

function Sparkle({
  className = '',
  size = 18,
  delay = '0s',
}: {
  className?: string;
  size?: number;
  delay?: string;
}) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      className={`absolute tk-anim ${className}`}
      style={{ animation: 'tk-twinkle 2.6s ease-in-out infinite', animationDelay: delay }}
    >
      <path d="M12 0 L14.5 9.5 L24 12 L14.5 14.5 L12 24 L9.5 14.5 L0 12 L9.5 9.5 Z" fill="#22A65B" />
    </svg>
  );
}

export default function RenovationScene() {
  return (
    <div
      aria-hidden="true"
      data-testid="renovation-scene"
      className="relative w-full overflow-hidden select-none h-[200px] sm:h-[310px] lg:h-[470px]"
    >
      <style>{`
        @keyframes tk-float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
        @keyframes tk-twinkle { 0%, 100% { opacity: 0.15; transform: scale(0.6); } 50% { opacity: 1; transform: scale(1); } }
        @keyframes tk-hammer { 0%, 38% { transform: rotate(20deg); } 50% { transform: rotate(-34deg); } 60%, 100% { transform: rotate(20deg); } }
        @keyframes tk-hit { 0%, 48% { opacity: 0; transform: scale(0.3); } 53% { opacity: 1; transform: scale(1.15); } 62%, 100% { opacity: 0; transform: scale(0.4); } }
        @media (prefers-reduced-motion: reduce) { .tk-anim { animation: none !important; } }
      `}</style>

      {/* sky gradient */}
      <div className="absolute inset-0 bg-[radial-gradient(130%_130%_at_50%_-30%,#E7EAEC_0%,#F1EDE4_50%,#F5F0E8_100%)]" />

      {/* fixed-size stage, scaled down responsively */}
      <div className="absolute left-1/2 top-0 w-[1200px] h-[470px] -translate-x-1/2 origin-top scale-[0.43] sm:scale-[0.66] lg:scale-100">
        {/* conveyor band, centered horizon */}
        <div className="absolute left-[-700px] right-[-700px] top-[268px] rotate-[-2deg]">
          <div className="h-14 bg-[#EFE9DC] border-t border-white/70" />
          <div className="relative h-11 bg-[#E3DBCB] shadow-[0_60px_90px_-50px_rgba(20,20,20,0.45)]">
            <div className="absolute inset-x-0 top-4 flex gap-24 px-10">
              {Array.from({ length: 24 }).map((_, i) => (
                <div key={i} className="w-2 h-2 rounded-full bg-[#141414]/10 shrink-0" />
              ))}
            </div>
          </div>
        </div>

        {/* worn house, waiting on the belt */}
        <div
          className="absolute left-[110px] top-[136px] rotate-[-3deg] tk-anim"
          style={{ animation: 'tk-float 7s ease-in-out infinite' }}
        >
          <ShabbyHouse />
        </div>

        {/* renovation in progress, dead center */}
        <div className="absolute left-[505px] top-[116px]">
          <div className="relative">
            <MidRenoHouse />
            {/* swinging hammer, pivoting at the grip */}
            <div
              className="absolute -top-[58px] right-[-30px] tk-anim origin-[55%_92%]"
              style={{ animation: 'tk-hammer 1.8s ease-in-out infinite' }}
            >
              <Hammer />
            </div>
            {/* impact flash at the roof ridge */}
            <svg
              viewBox="0 0 24 24"
              width={30}
              height={30}
              className="absolute top-[-4px] right-[52px] tk-anim"
              style={{ animation: 'tk-hit 1.8s ease-in-out infinite' }}
            >
              <path
                d="M12 0 L14.5 9.5 L24 12 L14.5 14.5 L12 24 L9.5 14.5 L0 12 L9.5 9.5 Z"
                fill="#F0B429"
              />
            </svg>
          </div>
        </div>

        {/* renovated house, shining on the way out */}
        <div
          className="absolute left-[900px] top-[122px] tk-anim"
          style={{ animation: 'tk-float 7s ease-in-out infinite', animationDelay: '1.2s' }}
        >
          <div className="relative">
            <ShinyHouse />
            <Sparkle className="-top-6 left-2" size={20} />
            <Sparkle className="-top-2 right-0" size={14} delay="0.9s" />
            <Sparkle className="top-14 -left-7" size={12} delay="1.7s" />
            <Sparkle className="top-8 -right-8" size={16} delay="0.4s" />
            {/* verified badge */}
            <div className="absolute -top-4 -right-4 w-9 h-9 rounded-full bg-white shadow-[0_12px_24px_-8px_rgba(20,20,20,0.3)] flex items-center justify-center">
              <svg viewBox="0 0 16 16" className="w-5 h-5">
                <circle cx="8" cy="8" r="8" fill="#22A65B" />
                <path d="M4.5 8.5l2.2 2.2L11.5 6" stroke="#fff" strokeWidth="1.8" fill="none" strokeLinecap="round" />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
